/**
 * Global Cart Request Manager
 * Resolves 429 (Too Many Requests) errors
 * 
 * Features:
 * 1. Request queue management - Ensures only one cart request is processed at a time
 * 2. Debounce mechanism - Prevents multiple requests from rapid consecutive clicks
 * 3. Error handling - Unified handling for stock insufficiency and other errors, displayed to users
 * 4. Auto retry - Automatically retries on 429 errors
 */

(function() {
  'use strict';
  
  // Global cart request manager
  window.CartRequestManager = {
    // Request queue
    queue: [],
    // Whether a request is currently being processed
    isProcessing: false,
    // Debounce timers
    debounceTimers: {},
    // Maximum retry count
    maxRetries: 2,
    // Retry delay (milliseconds)
    retryDelay: 2000,
    // Minimum interval between requests (milliseconds) - optimized for response speed
    minRequestInterval: 400,
    // Last request time
    lastRequestTime: 0,
    
    /**
     * Add request to queue (with debounce)
     * @param {string} key - Unique identifier for the request (e.g., lineID)
     * @param {function} requestFn - Function that executes the request
     * @param {number} debounceMs - Debounce delay (milliseconds)
     */
    addRequest: function(key, requestFn, debounceMs) {
      var self = this;
      debounceMs = debounceMs || 600;
      
      // Clear previous debounce timer for this key
      if (this.debounceTimers[key]) {
        clearTimeout(this.debounceTimers[key]);
      }
      
      // Set new debounce timer
      this.debounceTimers[key] = setTimeout(function() {
        // Add request to queue
        self.queue.push({
          key: key,
          fn: requestFn,
          retries: 0
        });
        
        // Try to process the queue
        self.processQueue();
      }, debounceMs);
    },
    
    /**
     * Process request queue
     */
    processQueue: function() {
      var self = this;
      
      // If currently processing or queue is empty, return
      if (this.isProcessing || this.queue.length === 0) {
        return;
      }
      
      // Check time since last request
      var now = Date.now();
      var timeSinceLastRequest = now - this.lastRequestTime;
      
      // If minimum interval hasn't elapsed since last request, delay processing
      if (timeSinceLastRequest < this.minRequestInterval) {
        var delay = this.minRequestInterval - timeSinceLastRequest;
        console.log('[CartRequestManager] Waiting ' + delay + 'ms before processing next request');
        setTimeout(function() {
          self.processQueue();
        }, delay);
        return;
      }
      
      // Mark as processing
      this.isProcessing = true;
      this.lastRequestTime = Date.now();
      
      // Dequeue the first request
      var request = this.queue.shift();
      
      console.log('[CartRequestManager] Processing request:', request.key);
      
      // Execute request
      request.fn()
        .done(function(response) {
          console.log('[CartRequestManager] Request succeeded:', request.key);
          // Request succeeded, mark as complete
          self.isProcessing = false;
          // Process next request (will automatically respect minimum interval)
          self.processQueue();
        })
        .fail(function(xhr, status, error) {
          console.error('[CartRequestManager] Request failed:', request.key, status, xhr.status);
          
          // If 429 error and max retries not exceeded
          if (xhr.status === 429 && request.retries < self.maxRetries) {
            request.retries++;
            console.log('[CartRequestManager] 429 error, retrying in ' + self.retryDelay + 'ms... (' + request.retries + '/' + self.maxRetries + ')');
            
            // Put request back at the front of the queue
            self.queue.unshift(request);
            
            // Mark as complete, delay before processing next
            self.isProcessing = false;
            setTimeout(function() {
              self.processQueue();
            }, self.retryDelay);
          } else {
            // Other errors or retries exhausted
            if (xhr.status === 429) {
              console.error('[CartRequestManager] 429 error retry failed, please try again later');
              self.showErrorMessage('Too many requests, please wait a moment and try again');
            } else {
              console.error('[CartRequestManager] Request ultimately failed:', request.key);
              self.handleError(xhr, request.key);
            }
            
            // Mark as complete, process next request
            self.isProcessing = false;
            self.processQueue();
          }
        });
    },
    
    /**
     * Handle errors and display to user
     */
    handleError: function(xhr, requestKey) {
      var errorMessage = '';
      var isStockError = false;
      var availableQuantity = null;
      
      try {
        if (xhr.responseText) {
          var response = JSON.parse(xhr.responseText);
          if (response.description) {
            errorMessage = response.description;
            
            // Detect stock errors and extract available quantity
            // e.g.: "Only 5 items were added to your cart due to availability."
            if (errorMessage.includes('items were added') || errorMessage.includes('item was added')) {
              isStockError = true;
              var match = errorMessage.match(/Only (\d+) item/i);
              if (match) {
                availableQuantity = parseInt(match[1]);
              }
            }
          } else if (response.message) {
            errorMessage = response.message;
          }
        }
      } catch (e) {
        if (xhr.status === 429) {
          errorMessage = 'Too many requests, please try again later';
        } else {
          errorMessage = 'Cart update failed, please try again later';
        }
      }
      
      console.error('[CartRequestManager] Error message:', errorMessage);
      
      // If stock error, display below the product
      if (isStockError && availableQuantity !== null) {
        this.showStockError(requestKey, errorMessage, availableQuantity);
      } else if (xhr.status !== 429) {
        // Only show global notification for non-429 errors
        this.showErrorMessage(errorMessage);
      }
    },
    
    /**
     * Display stock error below the product
     */
    showStockError: function(requestKey, message, availableQuantity) {
      // Extract lineID from requestKey (format: updateCart_1)
      var lineID = requestKey.replace('updateCart_', '');
      
      // Find the corresponding product row
      var $lineItem = $('[data-line-item="' + lineID + '"]');
      
      if ($lineItem.length) {
        // Remove old error message
        $lineItem.find('.stock-error-message').remove();
        
        // Create error message element
        var $errorMsg = $('<div class="stock-error-message" style="' +
          'color: #d32f2f;' +
          'font-size: 12px;' +
          'margin-top: 5px;' +
          'animation: fadeIn 0.3s;' +
        '"></div>').text(message);
        
        // Insert below the quantity input
        $lineItem.find('.product-quantity-box').after($errorMsg);
        
        // Update quantity input to available quantity
        $lineItem.find('.quantity-input').val(availableQuantity);
        
        // Fade out and remove after 3 seconds
        setTimeout(function() {
          $errorMsg.fadeOut(300, function() {
            $(this).remove();
          });
        }, 8000);
        
        // Trigger a cart update to sync the correct quantity
        setTimeout(function() {
          if (window.PXUTheme && window.PXUTheme.jsAjaxCart) {
            window.PXUTheme.jsAjaxCart.updateView();
          }
        }, 500);
      }
    },
    
    /**
     * Show error message (global notification)
     */
    showErrorMessage: function(message) {
      // Remove old error message
      $('.cart-global-error').remove();
      
      // Create error message element
      var $error = $('<div class="cart-global-error" style="' +
        'position: fixed;' +
        'top: 20px;' +
        'left: 50%;' +
        'transform: translateX(-50%);' +
        'background: #f44336;' +
        'color: white;' +
        'padding: 15px 25px;' +
        'border-radius: 4px;' +
        'box-shadow: 0 2px 10px rgba(0,0,0,0.2);' +
        'z-index: 99999;' +
        'max-width: 90%;' +
        'text-align: center;' +
        'animation: slideDown 0.3s ease-out;' +
      '"></div>').text(message);
      
      $('body').append($error);
      
      // Auto remove after 3 seconds
      setTimeout(function() {
        $error.fadeOut(300, function() {
          $(this).remove();
        });
      }, 3000);
    },
    
    /**
     * Wrap Ajax request as Promise (compatible with jQuery Deferred)
     */
    wrapAjaxRequest: function(ajaxOptions) {
      return $.ajax(ajaxOptions);
    }
  };
  
  // Add CSS animations
  if (!$('#cart-manager-styles').length) {
    $('<style id="cart-manager-styles">' +
      '@keyframes slideDown {' +
      '  from { top: -100px; opacity: 0; }' +
      '  to { top: 20px; opacity: 1; }' +
      '}' +
      '@keyframes fadeIn {' +
      '  from { opacity: 0; transform: translateY(-10px); }' +
      '  to { opacity: 1; transform: translateY(0); }' +
      '}' +
    '</style>').appendTo('head');
  }
  
  console.log('[CartRequestManager] Initialized');
})();